/**
 * <wdka-clock> — analog + digital clock for the top-right corner.
 *
 * Self-contained: markup, styles and ticking all live inside a shadow root,
 * so its styles are fully encapsulated — they can't leak onto the page and
 * page styles can't clobber them. Drop `<wdka-clock></wdka-clock>` into the
 * DOM and it starts/stops itself via the element lifecycle.
 *
 * Hours and minutes are gradient-filled half-disc wedges that rotate; the
 * seconds hand is a short rim tick. The gradient `<defs>` live in the same
 * shadow root, so the `fill: url(#…)` references resolve within it.
 */

const TEMPLATE = `
  <div class="clock__text"></div>
  <div class="clock__seconds">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <line x1="15" y1="50" x2="5" y2="50"></line>
    </svg>
  </div>
  <div class="clock__minutes">
    <svg class="clockface" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <g><path d="m50 0a50 50 0 0 0 -50 50h100a50 50 0 0 0 -50-50z"></path></g>
    </svg>
  </div>
  <div class="clock__hours">
    <svg class="clockface" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <g><path d="m50 0a50 50 0 0 0 -50 50h100a50 50 0 0 0 -50-50z"></path></g>
    </svg>
  </div>
  <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="hours-gradient">
        <stop offset="0%" stop-color="transparent"></stop>
        <stop offset="100%" stop-color="palevioletred"></stop>
      </linearGradient>
      <linearGradient id="minutes-gradient">
        <stop offset="0%" stop-color="steelblue"></stop>
        <stop offset="100%" stop-color="transparent"></stop>
      </linearGradient>
    </defs>
  </svg>
`;

const STYLE = `
  :host {
    --clock-size: 75px;

    display: block;
    position: absolute;
    top: 20px;
    right: 20px;
    width: var(--clock-size);
    height: var(--clock-size);
    border-radius: 50%;
    text-align: center;
    line-height: var(--clock-size);
    color: whitesmoke;
    z-index: 10;
  }

  @media (min-width: 768px) {
    :host {
      --clock-size: 80px;
      top: 50px;
      right: 50px;
    }
  }

  @media (min-width: 1280px) {
    :host {
      --clock-size: 100px;
      font-size: 1.4rem;
    }
  }

  @media (min-width: 1920px) {
    :host {
      --clock-size: 180px;
      font-size: 2.25rem;
      letter-spacing: 0.2rem;
      font-weight: bold;
    }
  }

  .clock__hours,
  .clock__minutes,
  .clock__seconds {
    width: var(--clock-size);
    height: var(--clock-size);
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 50%;
    transition: transform 0.2s;
    transition-timing-function: cubic-bezier(0.62, 0.99, 0.85, 1.24);
  }

  .clockface {
    fill: url("#minutes-gradient");
  }

  .clock__hours .clockface {
    fill: url("#hours-gradient");
  }

  .clock__seconds {
    transition: none;
    z-index: 20;
  }

  .clock__seconds svg {
    stroke: rgba(245, 245, 245, 0.7);
    stroke-width: 3px;
  }

  .clock__text {
    position: relative;
    z-index: 200;
  }
`;

function pad2(value) {
  return value < 10 ? `0${value}` : String(value);
}

export class WdkaClock extends HTMLElement {
  #interval = null;
  #hours = null;
  #minutes = null;
  #seconds = null;
  #text = null;

  connectedCallback() {
    if (!this.shadowRoot) {
      const root = this.attachShadow({ mode: "open" });
      root.innerHTML = `<style>${STYLE}</style>${TEMPLATE}`;
    }

    const root = this.shadowRoot;
    this.#hours = root.querySelector(".clock__hours");
    this.#minutes = root.querySelector(".clock__minutes");
    this.#seconds = root.querySelector(".clock__seconds");
    this.#text = root.querySelector(".clock__text");

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#interval);
    this.#interval = null;
  }

  #tick() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();

    const hoursInSeconds = (hours % 12) * 3600 + minutes * 60 + seconds;
    const hoursDegrees = (hoursInSeconds / 43200) * 360 - 90;
    const minutesDegrees = ((minutes * 60 + seconds) / 3600) * 360 + 90;
    const secondsDegrees = (seconds / 60) * 360 + 90;

    this.#text.textContent = `${pad2(hours)}:${pad2(minutes)}`;
    this.#hours.style.transform = `rotate(${hoursDegrees}deg)`;
    this.#minutes.style.transform = `rotate(${minutesDegrees}deg)`;
    this.#seconds.style.transform = `rotate(${secondsDegrees}deg)`;
  }
}

if (!customElements.get("wdka-clock")) {
  customElements.define("wdka-clock", WdkaClock);
}
